import React, { useCallback, useEffect, useRef } from "react";
import { useTheme } from "styled-components";
import { IGroup } from "../../../classes/interfaces";
import Icon from "../../../components/ui/Icon";
import { resolveGroupIcon } from "../../../helpers/groupIcon.helper";
import { Nav, SearchRow, SearchBox, SearchInput, ClearButton, ChipsRow, Chip, ChipIconBadge } from "./GroupTabs.styles";

interface GroupTabsProps {
  groups: IGroup[] | undefined;
  query: string;
  onQueryChange: (query: string) => void;
}

function scrollToId(id: string) {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
}

export default function GroupTabs({ groups, query, onQueryChange }: GroupTabsProps) {
  const theme = useTheme();
  const [value, setValue] = React.useState(0);
  const tabForce = useRef(false);
  const chipsRowRef = useRef<HTMLDivElement>(null);

  // A busca encolhe a lista de grupos: um índice selecionado antes do filtro
  // pode cair fora dela, e aí nenhum chip ficaria marcado. Nesse caso volta ao
  // primeiro — o scrollspy corrige na primeira rolagem.
  const activeIndex = value < (groups?.length ?? 0) ? value : 0;

  // Mantém o chip ativo visível na faixa horizontal quando a seleção muda
  // (scrollspy da rolagem vertical ou clique). Rola só o container dos chips
  // (scrollTo horizontal) — scrollIntoView também rolaria a página na
  // vertical e brigaria com o scroll do usuário.
  useEffect(() => {
    const row = chipsRowRef.current;
    const chip = row?.children[activeIndex] as HTMLElement | undefined;
    if (!row || !chip || typeof row.scrollTo !== "function") return;
    const target = chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollTo({ left: target, behavior: "smooth" });
  }, [activeIndex]);

  const handleChange = (newValue: number) => {
    tabForce.current = true;
    try {
      setValue(newValue);

      if (groups) {
        const id = groups[newValue].id;
        if (id) {
          scrollToId(`group${id}`);
        }
      }
    } finally {
      setTimeout(() => (tabForce.current = false), 1000);
    }
  };

  // Scrollspy: acompanha a rolagem vertical e marca o grupo visível.
  // A fixação da barra é 100% via CSS (Nav é position: sticky) — NÃO setar
  // position: fixed inline aqui: fixed sem left/width encolhe a Nav para a
  // largura do conteúdo (~2000px), o ChipsRow perde o overflow e o
  // auto-scroll horizontal do chip ativo para de funcionar.
  const myFunction = useCallback(() => {
    if (!tabForce.current) {
      let index: number | null = null;
      groups?.forEach((group) => {
        const id = `group${group.id}`;
        const groupHeader = document.getElementById(id);
        if (groupHeader) {
          const pageYOffset = window.pageYOffset;
          const groupSticky = groupHeader.offsetTop;
          if (pageYOffset > groupSticky - 60) {
            const visible = groups.filter((g) => g.id === group.id);
            if (visible.length > 0) {
              index = groups.indexOf(visible[0]);
            }
          }
        }
      });

      // index !== null (e não `if (index)`): 0 é um índice válido — senão
      // voltar ao topo nunca re-seleciona o primeiro grupo.
      if (index !== null && index !== value) {
        setValue(index);
      }
    }
  }, [groups, tabForce, value]);

  useEffect(() => {
    if (groups) {
      window.onscroll = function () {
        myFunction();
      };
    }
  }, [groups, myFunction]);

  if (groups === undefined) {
    return null;
  }

  return (
    <Nav id="myHeader">
      <SearchRow>
        <SearchBox>
          <Icon name="search" size={18} color={theme.tokens.colors.muted} />
          <SearchInput
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Buscar no cardápio…"
            aria-label="Buscar no cardápio"
          />
          {query && (
            <ClearButton type="button" aria-label="Limpar busca" onClick={() => onQueryChange("")}>
              <Icon name="x" size={13} strokeWidth={2.4} color={theme.tokens.colors.muted} />
            </ClearButton>
          )}
        </SearchBox>
      </SearchRow>
      <ChipsRow ref={chipsRowRef}>
        {groups.map((group, index) => (
          <Chip
            key={group.id}
            $active={activeIndex === index}
            aria-current={activeIndex === index ? "true" : undefined}
            type="button"
            onClick={() => handleChange(index)}
          >
            <ChipIconBadge $active={activeIndex === index}>
              <Icon name={resolveGroupIcon(group)} size={14} strokeWidth={2} />
            </ChipIconBadge>
            {group.name}
          </Chip>
        ))}
      </ChipsRow>
    </Nav>
  );
}
